import axios from 'axios';
import { checkFoundation, isEmpty } from './common';
import { IProgressEventPayload } from '../types/events';
import { Location, OperationType, OrganizationType } from '../types/enums';
import { Model, PageModel } from 'survey-core';
import { pick } from 'lodash';
import { SELF_SERVE_VALUE } from '../components/questionTypes/MuiAdditionalProducts';
import { store } from '../../../store';
import { SubscriptionTiers } from '@pec/aion-ui-core/enums/Subscription';

type CustomPageModel = PageModel & {
  stepName?: string;
};

const getStepNumber = (operationType: OperationType, page?: CustomPageModel, isFoundation?: boolean) => {
  if (operationType === OperationType.Upgrade && !isFoundation) {
    return page ? page.num + 1 : 1;
  }
  return page?.num || 1;
};

export const updateLead = async (
  model: Model,
  data: Record<string, any>,
  page?: CustomPageModel,
  product?: { ids: string[]; codes: string[] }
) => {
  const {
    organization: { organization, ssqId },
    mainSubscription: { mainSubscription }
  } = store.getState();
  const info = model.getVariable('info');
  const discount = model.getVariable('discount');

  if (!organization || !mainSubscription || !info) return;

  try {
    const productIds = Object.values(pick(data, ['subscriptionSelect', 'additionalProducts'])).filter(
      productId => productId && productId !== SELF_SERVE_VALUE
    );
    const productCodes = productIds.map(productId =>
      model.runExpression(`expandChoicesByUrl('${productId}', productCode)`)
    );

    if (data.subscriptionSelect && model.runExpression('getJoiningFee()') > 0) {
      productIds.push(model.runExpression("getJoiningFee('id')"));
      productCodes.push(model.runExpression("getJoiningFee('productCode')"));
    }

    const eliteChosen =
      data['subscriptionSelect'] &&
      model.runExpression(`expandChoicesByUrl('${data['subscriptionSelect']}', subscriptionTier)`) ===
        SubscriptionTiers.Elite;
    const GBElitePackage = eliteChosen && mainSubscription.countryISO2 === Location.GB;
    const isFoundation = checkFoundation(info.currentSubscription);
    let applicationType = data.applicationType || info.applicationType;
    let assessmentType = data.assessmentType || info.assessmentType;
    const employeeCount = isEmpty(data['subcontractorEmployeeCount']) ? info.employeeCount : data['totalEmployeeCount'];
    const subcontractorEmployeeCount = isEmpty(data['subcontractorEmployeeCount'])
      ? info.subcontractorEmployeeCount
      : data['subcontractorEmployeeCount'];

    if (isFoundation && data.organizationType === OrganizationType.Supplier) {
      applicationType = OrganizationType.Supplier;
      if (info.assessmentType) {
        assessmentType = 'Direct Application';
      }
    }

    const body: IProgressEventPayload = {
      operationType: isFoundation ? OperationType.SignUp : info.operationType,
      stepNumber: getStepNumber(info.operationType, page, isFoundation),
      stepName: page?.stepName || 'UpgradeStart',
      organization: {
        organizationId: organization.id,
        companyId: Number(ssqId),
        currentSubscriptionTier: mainSubscription.description,
        url: window.location.href,
        totalEmployeeCount: employeeCount,
        subcontractorEmployeeCount,
        dataSharingConsent: GBElitePackage ? Boolean(data['dataSharingConsent']) : null,
        expirationDateUtc: info.expiryDate,
        package: {
          location: mainSubscription.countryISO2,
          companyType: data.organizationType || info.companyType,
          applicationType,
          assessmentType,
          discountCode: discount && data['discountCode'],
          addToSupplyChainClientId: discount?.clientId,
          productCodes: product?.codes || productCodes,
          productIds: product?.ids || productIds
        }
      }
    };

    await axios.post<string>('/api/v1/progress', body);
  } catch (e) {
    throw e;
  }
};
